import { FactoryIcon } from '@phosphor-icons/react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useApproverPendingQuery } from '@/hooks/useLogsheetApproverQueries';
import { useCheckerPendingQuery } from '@/hooks/useLogsheetCheckerQueries';
import { useUnreadCountQuery } from '@/hooks/useNotificationsQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { readSidebarCollapsed, writeSidebarCollapsed } from '@/lib/storage/sidebar-state';
import { cn } from '@/lib/utils/cn';
import { NAV_GROUPS, type NavGroup, type NavItem } from '@/lib/utils/nav-items';

interface SidebarProps {
  readonly className?: string;
  /** When true, renders as a mobile Sheet drawer triggered by aryan:sidebar:mobile:open events.
   *  The desktop collapsed/expanded toggle still works via aryan:sidebar:toggle. */
  readonly mobileDrawer?: boolean;
}

/**
 * Collapsible left sidebar. Width 240px expanded, 64px collapsed (icon-only).
 * Collapsed state is persisted in localStorage via `sidebar-state` helpers.
 *
 * The collapse-toggle button lives in the Topbar (per UI Guide §10.1) and dispatches a custom
 * window event `'aryan:sidebar:toggle'` that this component listens for. This decouples the two
 * components so plan 03-04 can mount them in a router context without a shared parent prop.
 *
 * Mobile drawer: AppShell renders a second <Sidebar mobileDrawer /> instance that listens for
 * `'aryan:sidebar:mobile:open'` and renders inside a shadcn Sheet sliding from the left.
 * The desktop sidebar instance is hidden via `hidden md:block` wrapper in AppShell.
 *
 * Phase 13 Plan 13-02: Checker Queue item shows a count badge driven by the pending list query
 * (D-121). Badge renders only when count > 0 and permission `logsheet.checker.review` is held.
 */

/**
 * REVIEW FF-016: tighten the prefix-match rule.
 *
 * The old rule was `p === required || p.startsWith(`${required}.`)` for ANY
 * `required` string. That makes single-segment prefixes dangerous — adding a
 * future nav item with `permission: 'logsheet'` would silently grant access
 * to everyone holding ANY `logsheet.*.*` permission. The intent of the
 * sidebar gate is to test a SPECIFIC permission code, not a top-level module.
 *
 * Rule now:
 *  - `required` must be a full permission code (e.g. `users.read`,
 *    `logsheet.maker.create`) — at least one `.` — OR
 *  - it must be one of the documented module roots in `MODULE_ROOT_ALLOWLIST`
 *    that the user has at least one `${root}.*` permission for. The allowlist
 *    is the small set of legacy nav items whose `permission` is just the
 *    module name (`sites`, `machines`, etc.) — see `nav-items.ts`. Anything
 *    not on this list is rejected even if it has no dot, preventing accidental
 *    over-grant when a future maintainer adds `permission: 'logsheet'`.
 *
 * An item with no `permission` field is always shown.
 */
const MODULE_ROOT_ALLOWLIST: ReadonlySet<string> = new Set([
  'sites',
  'machines',
  'machineTypes',
  'users',
  'roles',
]);

function filterNavByPermissions(
  items: readonly NavItem[],
  permissions: readonly string[],
): NavItem[] {
  return items.filter((item) => {
    const required = item.permission;
    if (required == null) return true;
    // Full permission code (a.b or a.b.c) — exact-match OR module-level prefix.
    if (required.includes('.')) {
      return permissions.some((p) => p === required || p.startsWith(`${required}.`));
    }
    // Single-segment — only allowed when on the documented allowlist.
    if (!MODULE_ROOT_ALLOWLIST.has(required)) return false;
    return permissions.some((p) => p === required || p.startsWith(`${required}.`));
  });
}

/**
 * Filters every group's items through the permission + role gates, then drops
 * groups that become empty. Group order and item order are preserved.
 */
function filterNavGroups(
  groups: readonly NavGroup[],
  permissions: readonly string[],
  roles: readonly string[],
): NavGroup[] {
  return groups
    .map((g) => ({
      label: g.label,
      items: filterNavByRoles(filterNavByPermissions(g.items, permissions), roles),
    }))
    .filter((g) => g.items.length > 0);
}

/**
 * Phase 23 (Plan 23-03) D-243 — role-based filter applied AFTER the permission
 * filter. An item with no `role` field passes through; an item with `role: 'X'`
 * is shown only when `roles.includes('X')` (exact match — role names are stable
 * literals like "Super Admin" mirrored from `AuthConstants.RoleNames`).
 */
function filterNavByRoles(
  items: readonly NavItem[],
  roles: readonly string[],
): NavItem[] {
  return items.filter((item) => {
    const required = item.role;
    if (required == null) return true;
    return roles.includes(required);
  });
}

/**
 * Inner component for the Checker Queue nav link — renders the label with a live
 * count badge from the pending query. Extracted so the badge query only mounts
 * when the user actually has the `logsheet.checker.review` permission.
 */
function CheckerQueueLabel({ collapsed }: { readonly collapsed: boolean }): JSX.Element {
  const { data } = useCheckerPendingQuery({ page: 1, pageSize: 1 });
  const count = data?.total ?? 0;

  if (collapsed) return <></>;

  return (
    <span className="flex flex-1 items-center justify-between truncate">
      <span className="truncate">Checker Queue</span>
      {count > 0 && (
        <span
          className="ml-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground"
          data-testid="sidebar-nav-checker-queue-badge"
        >
          {count > 99 ? '99+' : String(count)}
        </span>
      )}
    </span>
  );
}

/**
 * Inner component for the Approver Queue nav link — renders the label with a live
 * count badge from the pending query. Extracted so the badge query only mounts
 * when the user actually has the `logsheet.approver.review` permission.
 */
function ApproverQueueLabel({ collapsed }: { readonly collapsed: boolean }): JSX.Element {
  const { data } = useApproverPendingQuery({ page: 1, pageSize: 1 });
  const count = data?.total ?? 0;

  if (collapsed) return <></>;

  return (
    <span className="flex flex-1 items-center justify-between truncate">
      <span className="truncate">Approver Queue</span>
      {count > 0 && (
        <span
          className="ml-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground"
          data-testid="sidebar-nav-approver-queue-badge"
        >
          {count > 99 ? '99+' : String(count)}
        </span>
      )}
    </span>
  );
}

/**
 * Inner component for the Alerts nav link — renders the label with a live
 * unread-count badge from the alerts unread-count query. Extracted so the
 * badge query only mounts when the user actually has `notifications.view`.
 */
function AlertsBadgeLabel({ collapsed }: { readonly collapsed: boolean }): JSX.Element {
  const { data } = useUnreadCountQuery({ enabled: true });
  const count = data?.count ?? 0;

  if (collapsed) return <></>;

  return (
    <span className="flex flex-1 items-center justify-between truncate">
      <span className="truncate">Alerts</span>
      {count > 0 && (
        <span
          className="ml-2 rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground"
          data-testid="sidebar-nav-alerts-badge"
        >
          {count > 99 ? '99+' : String(count)}
        </span>
      )}
    </span>
  );
}

/** Shared nav list used by both desktop sidebar and mobile drawer. */
function SidebarNav({
  collapsed,
  visibleGroups,
  hasCheckerReview,
  hasApproverReview,
  hasAlertsView,
  onNavClick,
}: {
  readonly collapsed: boolean;
  readonly visibleGroups: readonly NavGroup[];
  readonly hasCheckerReview: boolean;
  readonly hasApproverReview: boolean;
  readonly hasAlertsView: boolean;
  readonly onNavClick?: () => void;
}): JSX.Element {
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Sidebar">
      {visibleGroups.map((group, groupIdx) => (
        <div
          key={group.label}
          className={cn(groupIdx > 0 && (collapsed ? 'mt-2 pt-2 border-t border-sidebar-border/60' : 'mt-4'))}
          data-testid={`sidebar-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {!collapsed && (
            <div
              className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
              aria-hidden="true"
            >
              {group.label}
            </div>
          )}
          <ul className="space-y-1">
            {group.items.map((item) => {
              const Icon = item.icon;
              const isCheckerQueue = item.to === '/logsheets/checker/pending';
              const isApproverQueue = item.to === '/logsheets/approver/pending';
              const isAlertsNav = item.to === '/alerts';
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/dashboard'}
                    data-testid={item.testId ?? (
                      isCheckerQueue
                        ? 'sidebar-nav-checker-queue'
                        : isApproverQueue
                          ? 'sidebar-nav-approver-queue'
                          : undefined
                    )}
                    onClick={onNavClick}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                        'transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
                        collapsed && 'justify-center px-0',
                      )
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={18} weight="duotone" />
                    {!collapsed && isCheckerQueue && hasCheckerReview ? (
                      <CheckerQueueLabel collapsed={collapsed} />
                    ) : !collapsed && isApproverQueue && hasApproverReview ? (
                      <ApproverQueueLabel collapsed={collapsed} />
                    ) : !collapsed && isAlertsNav && hasAlertsView ? (
                      <AlertsBadgeLabel collapsed={collapsed} />
                    ) : (
                      !collapsed && <span className="truncate">{item.label}</span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function Sidebar({ className, mobileDrawer = false }: SidebarProps): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(() => readSidebarCollapsed());
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const { permissions, user } = useAuth();
  const userRoles = user?.roles;

  const visibleGroups = useMemo(
    () => filterNavGroups(NAV_GROUPS, permissions, userRoles ?? []),
    [permissions, userRoles],
  );

  useEffect(() => {
    writeSidebarCollapsed(collapsed);
  }, [collapsed]);

  // Desktop collapse/expand toggle
  useEffect(() => {
    const onToggle = (): void => {
      setCollapsed((prev) => !prev);
    };
    window.addEventListener('aryan:sidebar:toggle', onToggle);
    return () => {
      window.removeEventListener('aryan:sidebar:toggle', onToggle);
    };
  }, []);

  // Mobile drawer open trigger
  useEffect(() => {
    if (!mobileDrawer) return;
    const onOpen = (): void => {
      setMobileOpen(true);
    };
    window.addEventListener('aryan:sidebar:mobile:open', onOpen);
    return () => {
      window.removeEventListener('aryan:sidebar:mobile:open', onOpen);
    };
  }, [mobileDrawer]);

  const hasCheckerReview = permissions.includes('logsheet.checker.review');
  const hasApproverReview = permissions.includes('logsheet.approver.review');
  const hasAlertsView = permissions.includes('notifications.view');

  const brandBlock = (
    <div
      className={cn(
        'flex items-center gap-3 border-b border-sidebar-border px-4 py-4 h-14',
        collapsed && !mobileDrawer && 'justify-center px-0',
      )}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground shrink-0">
        <FactoryIcon size={18} weight="bold" />
      </div>
      {(!collapsed || mobileDrawer) && (
        <div className="flex flex-col">
          <span className="text-sm font-semibold leading-tight">Aryan O&amp;M</span>
          <span className="text-xs text-muted-foreground leading-tight">v2</span>
        </div>
      )}
    </div>
  );

  const footerBlock = (
    <div className="border-t border-sidebar-border p-3 text-xs text-muted-foreground">
      {(!collapsed || mobileDrawer) && <span>v2 · Phase 3</span>}
    </div>
  );

  // Mobile drawer variant — rendered inside Sheet, only shown on <md
  if (mobileDrawer) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-sidebar text-sidebar-foreground border-sidebar-border" showCloseButton={false}>
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          {brandBlock}
          <SidebarNav
            collapsed={false}
            visibleGroups={visibleGroups}
            hasCheckerReview={hasCheckerReview}
            hasApproverReview={hasApproverReview}
            hasAlertsView={hasAlertsView}
            onNavClick={() => { setMobileOpen(false); }}
          />
          {footerBlock}
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop sidebar variant
  return (
    <aside
      data-collapsed={collapsed}
      aria-label="Primary navigation"
      className={cn(
        'bg-sidebar text-sidebar-foreground border-r border-sidebar-border',
        'flex flex-col h-screen sticky top-0',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60',
        className,
      )}
    >
      {brandBlock}

      <SidebarNav
        collapsed={collapsed}
        visibleGroups={visibleGroups}
        hasCheckerReview={hasCheckerReview}
        hasApproverReview={hasApproverReview}
        hasAlertsView={hasAlertsView}
      />

      {footerBlock}
    </aside>
  );
}
